import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useMembershipPlanService, { MembershipPlan } from '../../../app/services/hooks/useMembershipPlanService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import SelectTwo from '../../../components/SelectTwo';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';

interface UserMembershipManagerProps {
    userId: number;
    userName: string;
    onClose: () => void;
    onSuccess: () => void;
}

interface UserMembershipInfo {
    has_membership: boolean;
    membership: {
        id: number;
        plan_id: number;
        plan_name: string;
        plan_code: string;
        payment_status: string;
        payment_status_display: string;
        start_date: string | null;
        end_date: string | null;
        days_remaining: number;
        days_until_period_reset: number;
        contracts_used: number;
        contracts_remaining: number;
        chatbot_messages_used: number;
        chatbot_messages_remaining: number;
        is_manual_assignment: boolean;
        assigned_by: string | null;
        is_valid: boolean;
    } | null;
    user: {
        id: number;
        name: string;
        email: string;
    };
}

const UserMembershipManager: React.FC<UserMembershipManagerProps> = ({
    userId,
    userName,
    onClose,
    onSuccess
}) => {
    const { requestConfirmation } = useReactConfirmAlert();
    const {
        getMembershipPlans,
        getUserMembership,
        assignMembership,
        revokeMembership,
        fetchingGetPlans,
        fetchingUserMembership,
        fetchingAssignMembership,
        fetchingRevokeMembership
    } = useMembershipPlanService();

    const [plans, setPlans] = useState<MembershipPlan[]>([]);
    const [membershipInfo, setMembershipInfo] = useState<UserMembershipInfo | null>(null);
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
    const [durationDays, setDurationDays] = useState<string>('');

    useEffect(() => {
        loadData();
    }, [userId]);

    const loadData = () => {
        // Cargar planes disponibles
        getMembershipPlans({ is_active: true }, {
            onSuccess: (res: any) => {
                setPlans(res.data.plans || []);
            }
        });

        // Cargar membresía actual del usuario
        getUserMembership(userId, {
            onSuccess: (res: any) => {
                setMembershipInfo(res.data);
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al cargar membresía');
            }
        });
    };

    const handleAssign = () => {
        if (!selectedPlanId) {
            toast.error('Selecciona un plan');
            return;
        }

        const selectedPlan = plans.find(p => p.id === selectedPlanId);

        requestConfirmation({
            title: '¿Asignar membresía?',
            message: `¿Asignar el plan "${selectedPlan?.name}" a ${userName}?`,
            buttons: {
                confirmButton: {
                    label: 'Sí, asignar',
                    onClick: () => {
                        assignMembership({
                            user_id: userId,
                            plan_id: selectedPlanId,
                            duration_days: durationDays ? parseInt(durationDays) : undefined
                        }, {
                            onSuccess: (res: any) => {
                                toast.success(res.message || 'Membresía asignada exitosamente');
                                loadData();
                                onSuccess();
                            },
                            onError: (res: any) => {
                                toast.error(res.message || 'Error al asignar membresía');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const handleRevoke = () => {
        if (!membershipInfo?.membership) return;

        requestConfirmation({
            title: '¿Revocar membresía?',
            message: `¿Revocar la membresía "${membershipInfo.membership.plan_name}" de ${userName}? El usuario perderá acceso a contratos gratis y al chatbot.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, revocar',
                    onClick: () => {
                        revokeMembership(userId, 'Revocada por administrador', {
                            onSuccess: (res: any) => {
                                toast.success(res.message || 'Membresía revocada exitosamente');
                                loadData();
                                onSuccess();
                            },
                            onError: (res: any) => {
                                toast.error(res.message || 'Error al revocar membresía');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return 'Sin fecha';
        return new Date(dateStr).toLocaleDateString('es-CL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatRemaining = (value: number) => {
        if (value === -1) return 'Ilimitado';
        return value.toString();
    };

    const isLoading = fetchingGetPlans || fetchingUserMembership;

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Información actual */}
            {membershipInfo?.has_membership && membershipInfo.membership ? (
                <div className="mb-4">
                    <h6 className="mb-3" style={{ color: '#001849' }}>
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Membresía Activa
                    </h6>
                    
                    <div 
                        className="p-3 rounded"
                        style={{ 
                            background: 'linear-gradient(135deg, #e6f0ff 0%, #f0f4ff 100%)',
                            border: '1px solid #001849'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <h5 className="mb-1" style={{ color: '#001849', fontWeight: 600 }}>
                                    {membershipInfo.membership.plan_name}
                                </h5>
                                {membershipInfo.membership.is_manual_assignment && (
                                    <span className="badge bg-info" style={{ fontSize: '0.7rem' }}>
                                        Asignación manual
                                    </span>
                                )}
                            </div>
                            <span className={`badge ${membershipInfo.membership.is_valid ? 'bg-success' : 'bg-danger'}`}>
                                {membershipInfo.membership.is_valid ? 'Activa' : 'Inactiva'}
                            </span>
                        </div>

                        <div className="row">
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Inicio</small>
                                <span>{formatDate(membershipInfo.membership.start_date)}</span>
                            </div>
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Vencimiento</small>
                                <span>
                                    {membershipInfo.membership.end_date 
                                        ? formatDate(membershipInfo.membership.end_date)
                                        : 'De por vida'}
                                </span>
                            </div>
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Días restantes</small>
                                <span className="fw-bold">
                                    {membershipInfo.membership.days_remaining === -1 
                                        ? 'Ilimitado' 
                                        : `${membershipInfo.membership.days_remaining} días`}
                                </span>
                            </div>
                            <div className="col-6 mb-2">
                                <small className="text-muted d-block">Reinicio de período</small>
                                <span>{membershipInfo.membership.days_until_period_reset} días</span>
                            </div>
                        </div>

                        <hr />

                        <div className="row">
                            <div className="col-6">
                                <small className="text-muted d-block">Contratos usados (este mes)</small>
                                <span className="fw-bold">
                                    {membershipInfo.membership.contracts_used} 
                                    <span className="text-muted ms-1">
                                        / {formatRemaining(membershipInfo.membership.contracts_remaining + membershipInfo.membership.contracts_used)}
                                    </span>
                                </span>
                            </div>
                            <div className="col-6">
                                <small className="text-muted d-block">Mensajes chatbot (este mes)</small>
                                <span className="fw-bold">
                                    {membershipInfo.membership.chatbot_messages_used}
                                    <span className="text-muted ms-1">
                                        / {formatRemaining(membershipInfo.membership.chatbot_messages_remaining + membershipInfo.membership.chatbot_messages_used)}
                                    </span>
                                </span>
                            </div>
                        </div>

                        {membershipInfo.membership.assigned_by && (
                            <div className="mt-3">
                                <small className="text-muted">
                                    <i className="fas fa-user me-1"></i>
                                    Asignado por: {membershipInfo.membership.assigned_by}
                                </small>
                            </div>
                        )}
                    </div>

                    {/* Botón revocar */}
                    <div className="mt-3">
                        <ButtonGeneral
                            onClick={handleRevoke}
                            title="Revocar membresía"
                            variant="danger"
                            size="small"
                            icon="fa-times"
                            iconPosition="left"
                            loading={fetchingRevokeMembership}
                        />
                    </div>
                </div>
            ) : (
                <div className="mb-4">
                    <div 
                        className="p-4 rounded text-center"
                        style={{ background: '#f8f9fa', border: '1px dashed #dee2e6' }}
                    >
                        <i className="fas fa-user-slash text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                        <p className="mb-0 text-muted">Este usuario no tiene membresía activa</p>
                        <small className="text-muted">
                            Debe pagar por cada contrato y no tiene acceso al chatbot
                        </small>
                    </div>
                </div>
            )}

            {/* Asignar nueva membresía */}
            {!membershipInfo?.has_membership && (
                <div>
                    <h6 className="mb-3" style={{ color: '#001849' }}>
                        <i className="fas fa-plus-circle me-2"></i>
                        Asignar Membresía
                    </h6>

                    <div className="mb-3">
                        <label className="form-label">Plan</label>
                        <SelectTwo
                            inputValue={selectedPlanId ? selectedPlanId.toString() : ''}
                            onChange={(option) => {
                                setSelectedPlanId(option ? parseInt(option.value) : null);
                                // Pre-llenar duración del plan
                                const plan = plans.find(p => p.id === parseInt(option?.value || '0'));
                                if (plan) {
                                    setDurationDays(plan.duration_days.toString());
                                }
                            }}
                            options={plans.map(p => ({
                                value: p.id.toString(),
                                label: `${p.name} - $${p.price.toLocaleString('es-CL')}${p.duration_days > 0 ? ` (${p.duration_days} días)` : ' (De por vida)'}`
                            }))}
                            placeholder="Selecciona un plan..."
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Duración (días)</label>
                        <input
                            type="number"
                            className="form-control"
                            value={durationDays}
                            onChange={(e) => setDurationDays(e.target.value)}
                            placeholder="Dejar vacío para usar la duración del plan"
                        />
                        <small className="text-muted">
                            0 = De por vida. Dejar vacío para usar la duración predefinida del plan.
                        </small>
                    </div>

                    <div className="d-flex gap-2">
                        <ButtonGeneral
                            onClick={handleAssign}
                            title="Asignar membresía"
                            variant="primary"
                            size="small"
                            icon="fa-check"
                            iconPosition="left"
                            loading={fetchingAssignMembership}
                            disabled={!selectedPlanId}
                        />
                        <ButtonGeneral
                            onClick={onClose}
                            title="Cancelar"
                            variant="secondary"
                            size="small"
                        />
                    </div>
                </div>
            )}

            {/* Botón cerrar si tiene membresía */}
            {membershipInfo?.has_membership && (
                <div className="d-flex justify-content-end mt-4">
                    <ButtonGeneral
                        onClick={onClose}
                        title="Cerrar"
                        variant="secondary"
                        size="small"
                    />
                </div>
            )}
        </div>
    );
};

export default UserMembershipManager;
